import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { 
  OfferMerchantAdmin, 
  OfferResponse,
  QueryFindAdminOffersArgs
} from "../../../../schemaTypes";
import { newOfferSubscription } from "../../../Offer/FindOffers";

type FindAdminOffersQuery = {
  findAdminOffers: OfferResponse;
};

type FindAdminOffersQueryVariables = QueryFindAdminOffersArgs;

export const findAdminOffersQuery = gql`
  query FindAdminOffers(
    $page: Int!
    $pageSize: Int!
    $duration: String
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAdminOffers(
      page: $page
      pageSize: $pageSize
      duration: $duration
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      pageOffers{
        data {
          id
          title
          offerType
          redemptions
          startDate
          endDate
          status
          createdAt
          createdBy {
            admin {
              id
              merchantProfile {
                id
                companyName
              }
            }
          }
        }
        total
        page
        pageSize
        totalPages
      }
      redeemedOffers
      publishedOffers
      offerViews
      offerClicks
    }
  }
`;

newOfferSubscription;

export interface WithFindAdminOffers {
  offers: {
    pageOffers: {
      data: OfferMerchantAdmin[]
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    },
    redeemedOffers: number;
    publishedOffers: number;
    offerViews: number;
    offerClicks: number;
  }
  loading: boolean;
  loadMore: () => void;
  hasMoreOffers: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindAdminOffersQueryVariables;
  children: (data: WithFindAdminOffers) => React.ReactElement | null;
}


export const FindAdminOffers: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAdminOffersQuery,
    FindAdminOffersQueryVariables
  >
  (findAdminOffersQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const pageOffers = data?.findAdminOffers?.pageOffers || {
    data: [],
    total: 0,
    page: variables.page,
    pageSize: variables.pageSize,
    totalPages: 0,
  };

  const offers = {
    pageOffers,
    redeemedOffers: data?.findAdminOffers?.redeemedOffers ?? 0,
    publishedOffers: data?.findAdminOffers?.publishedOffers ?? 0,
    offerViews: data?.findAdminOffers?.offerViews ?? 0,
    offerClicks: data?.findAdminOffers?.offerClicks ?? 0,
  };

  const page = pageOffers.page;
  const pageSize = pageOffers.pageSize;
  const total = pageOffers.total;
  const totalPages = pageOffers.totalPages;
  const hasMoreOffers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreOffers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAdminOffers) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAdminOffers: {
            ...fetchMoreResult.findAdminOffers,
            pageOffers: {
              ...fetchMoreResult.findAdminOffers.pageOffers,
              data: [
                ...(prev?.findAdminOffers?.pageOffers?.data || []),
                ...(fetchMoreResult.findAdminOffers.pageOffers?.data || [])
              ]
            }
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreOffers]);

  return children({
    offers: offers as any,
    loading,
    loadMore,
    hasMoreOffers,
    page,
    pageSize,
    total,
    totalPages,
  });
};